/**
 * Matches backend Response.java: { data, message, status, timestamp }
 */
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status?: number;
  timestamp?: string;
}

/**
 * Matches backend PaginationResponse.java: { data, totalPages, totalElements, pageNum }
 * Used when Response.data is this object (paginated endpoints).
 */
export interface PaginationResponse<T = unknown> {
  data: T[];
  totalPages: number;
  totalElements: number;
  pageNum: number;
}

/**
 * Extract list, pageNum, totalPages from axios response for paginated endpoints.
 * Backend returns: Response { data: PaginationResponse { data, totalPages, pageNum } }
 * Axios gives: response.data = Response, so response.data.data = PaginationResponse.
 */
export function parsePaginatedResponse<T>(responseData: ApiResponse<PaginationResponse<T>> | undefined): {
  list: T[];
  pageNum: number;
  totalPages: number;
} {
  const pagination = responseData?.data;
  if (!pagination || typeof pagination !== 'object') {
    return { list: [], pageNum: 0, totalPages: 0 };
  }
  const list = Array.isArray(pagination.data) ? pagination.data : [];
  const pageNum = Number(pagination.pageNum);
  const totalPages = Number(pagination.totalPages);
  return {
    list,
    pageNum: Number.isFinite(pageNum) && pageNum >= 0 ? pageNum : 0,
    totalPages: Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0,
  };
}

/**
 * Extract direct list from axios response for non-paginated endpoints.
 * Backend returns: Response { data: T[] }
 */
export function parseListResponse<T>(responseData: ApiResponse<T[]> | undefined): T[] {
  const data = responseData?.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Extract user-facing error message from axios error (backend Response.message or fallback).
 */
export function getApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const res = (err as { response?: { data?: { message?: string } } }).response;
    const msg = res?.data?.message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Erreur';
}
